import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Button } from '@/components/ui/button';
import { ChevronDown } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { format } from 'date-fns';

interface Ticket {
  number?: string;
  incident_number?: string;
  task_number?: string;
  short_description?: string;
  description?: string;
  assignment_group?: string;
  caller?: string;
  caller_id?: string;
  resolved_at?: string;
  closed_at?: string;
  created?: string;
  opened?: string;
  opened_at?: string;
  created_at?: string;
  state?: string;
  status?: string;
  task_type?: string;
  priority?: string;
  [key: string]: any;
}

interface RepetitiveTicketsProps {
  incidents: any[];
  catalogTasks: any[];
  showFullAnalysis?: boolean;
}

// Add categorization function
function categorizeDescription(description: string): string {
  const lowerDesc = description.toLowerCase();
  
  // Define category patterns
  const categories: { [key: string]: RegExp[] } = {
    "Secrets Update": [
      /\b(?:secret|key|credential|token|password|api\s+key)\b/,
      /\b(?:rotate|update|change|expire)\s+(?:secret|key|credential)\b/,
      /\b(?:aws|azure|gcp)\s+(?:secret|key|credential)\b/
    ],
    "Files or Messages": [
      /\b(?:file|message|document|attachment|upload|download)\b/,
      /\b(?:missing|corrupt|failed)\s+(?:file|message)\b/,
      /\b(?:s3|bucket|storage)\s+(?:file|object)\b/
    ],
    "Stuck or Data Issues": [
      /\b(?:stuck|hanging|frozen|blocked|deadlock)\b/,
      /\b(?:data|database|db)\s+(?:issue|error|corrupt|missing)\b/,
      /\b(?:timeout|slow|performance|bottleneck)\b/
    ],
    "Redeployment": [
      /\b(?:redeploy|rollback|revert|undo)\b/,
      /\b(?:failed|unsuccessful)\s+(?:deploy|deployment)\b/,
      /\b(?:revert|rollback)\s+(?:changes|updates)\b/
    ],
    "Deployment": [
      /\b(?:deploy|deployment|release|version|update|upgrade)\b/,
      /\b(?:rollout|pipeline|ci\/cd)\b/,
      /\b(?:new|latest)\s+(?:version|release)\b/
    ],
    "Code Fix": [
      /\b(?:bug|fix|patch|hotfix|issue)\b/,
      /\b(?:code|programming|development)\s+(?:error|issue|problem)\b/,
      /\b(?:fix|resolve|correct)\s+(?:issue|bug|problem)\b/
    ]
  };

  // Check each category's patterns
  for (const [category, patterns] of Object.entries(categories)) {
    if (patterns.some(pattern => pattern.test(lowerDesc))) {
      return category;
    }
  }

  return "Other";
}

// Add new AI-based analysis functions
function analyzePatterns(descriptions: string[]): { pattern: string; confidence: number }[] {
  // Remove common noise words and normalize
  const normalizedDescriptions = descriptions.map(desc => 
    desc.toLowerCase()
      .replace(/\b(?:the|a|an|and|or|but|in|on|at|to|for|of|with|by|from)\b/g, '')
      .replace(/[^\w\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
  );

  // Find common word sequences
  const wordSequences = new Map<string, number>();
  normalizedDescriptions.forEach(desc => {
    const words = desc.split(' ');
    for (let i = 0; i < words.length - 1; i++) {
      const sequence = `${words[i]} ${words[i + 1]}`;
      wordSequences.set(sequence, (wordSequences.get(sequence) || 0) + 1);
    }
  });

  // Calculate confidence scores
  const patterns = Array.from(wordSequences.entries())
    .map(([sequence, count]) => ({
      pattern: sequence,
      confidence: count / descriptions.length
    }))
    .filter(p => p.confidence > 0.3) // Only keep patterns that appear in at least 30% of descriptions
    .sort((a, b) => b.confidence - a.confidence);

  return patterns;
}

function findCommonIssues(descriptions: string[]): string[] {
  const issues = new Set<string>();
  
  // Common issue patterns
  const issuePatterns = [
    { pattern: /\b(?:error|failed|failure|exception)\b/i, type: "Error" },
    { pattern: /\b(?:timeout|slow|delay|latency)\b/i, type: "Performance" },
    { pattern: /\b(?:missing|not found|not exist)\b/i, type: "Missing Resource" },
    { pattern: /\b(?:invalid|incorrect|wrong)\b/i, type: "Invalid Data" },
    { pattern: /\b(?:duplicate|already exists)\b/i, type: "Duplicate" },
    { pattern: /\b(?:permission|access denied|unauthorized)\b/i, type: "Access Issue" },
    { pattern: /\b(?:corrupt|damaged|broken)\b/i, type: "Corruption" },
    { pattern: /\b(?:conflict|collision|overlap)\b/i, type: "Conflict" },
    { pattern: /\b(?:outdated|deprecated|obsolete)\b/i, type: "Outdated" },
    { pattern: /\b(?:incomplete|partial|missing)\b/i, type: "Incomplete" }
  ];

  descriptions.forEach(desc => {
    issuePatterns.forEach(({ pattern, type }) => {
      if (pattern.test(desc)) {
        issues.add(type);
      }
    });
  });

  return Array.from(issues);
}

function generateHeading(tickets: Ticket[]): string {
  // Get all descriptions
  const descriptions = tickets
    .map(t => t.short_description || t.description || "")
    .filter(d => d.trim().length > 0);

  if (descriptions.length === 0) return "No Description";

  // Find the most common category
  const categories = descriptions.map(categorizeDescription);
  const categoryCounts = categories.reduce((acc, cat) => {
    acc[cat] = (acc[cat] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const mostCommonCategory = Object.entries(categoryCounts)
    .sort((a, b) => b[1] - a[1])[0][0];

  // Get a representative description
  const representativeDesc = descriptions[0];
  
  // Analyze patterns and issues
  const patterns = analyzePatterns(descriptions);
  const commonIssues = findCommonIssues(descriptions);
  
  // Generate a concise heading
  let heading = mostCommonCategory;
  
  // Add specific details based on the category and analysis
  if (mostCommonCategory === "Secrets Update") {
    if (representativeDesc.toLowerCase().includes("rotate")) {
      heading = "Secret Rotation";
    } else if (representativeDesc.toLowerCase().includes("expire")) {
      heading = "Secret Expiration";
    } else if (commonIssues.includes("Access Issue")) {
      heading = "Secret Access Issue";
    } else {
      heading = "Secret Update";
    }
  } else if (mostCommonCategory === "Files or Messages") {
    if (commonIssues.includes("Missing Resource")) {
      heading = "Missing Files/Messages";
    } else if (commonIssues.includes("Corruption")) {
      heading = "Corrupt Files/Messages";
    } else if (commonIssues.includes("Access Issue")) {
      heading = "File Access Issue";
    } else {
      heading = "File/Message Issue";
    }
  } else if (mostCommonCategory === "Stuck or Data Issues") {
    if (commonIssues.includes("Performance")) {
      heading = "Performance Issue";
    } else if (commonIssues.includes("Timeout")) {
      heading = "Timeout Issue";
    } else if (commonIssues.includes("Corruption")) {
      heading = "Data Corruption";
    } else {
      heading = "Data Issue";
    }
  } else if (mostCommonCategory === "Redeployment") {
    if (commonIssues.includes("Error")) {
      heading = "Failed Deployment Rollback";
    } else {
      heading = "Deployment Rollback";
    }
  } else if (mostCommonCategory === "Deployment") {
    if (commonIssues.includes("Error")) {
      heading = "Failed Deployment";
    } else if (commonIssues.includes("Timeout")) {
      heading = "Deployment Timeout";
    } else {
      heading = "New Deployment";
    }
  } else if (mostCommonCategory === "Code Fix") {
    if (commonIssues.includes("Error")) {
      heading = "Critical Bug Fix";
    } else if (commonIssues.includes("Outdated")) {
      heading = "Code Update";
    } else {
      heading = "Code Fix";
    }
  }

  // Add pattern-based details if available
  if (patterns.length > 0 && patterns[0].confidence > 0.5) {
    heading += ` - ${patterns[0].pattern}`;
  }

  return heading;
}

export function RepetitiveTickets({ incidents, catalogTasks, showFullAnalysis }: RepetitiveTicketsProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedDescriptions, setExpandedDescriptions] = useState<Record<string, boolean>>({});

  const toggleDescription = (id: string) => {
    setExpandedDescriptions(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  
  const repetitiveItems = useMemo(() => {
    const allTickets = [...incidents, ...catalogTasks];
    
    const groups: { [key: string]: Ticket[] } = {};
    
    allTickets.forEach(ticket => {
      const description = ticket.short_description || ticket.description || "";
      
      if (!description.trim()) return;
      
      const normalizedDesc = description
        .toLowerCase()
        .replace(/\b(?:\d{1,2}[:/-]\d{1,2}[:/-]\d{2,4}|\d+[,.]?\d*)\b/g, '')  
        .replace(/\b(?:inc|req|task|catalog|number)\d+\b/i, '')  
        .replace(/[^\w\s]/g, '')  
        .split(' ')
        .filter(word => word.length > 2)  
        .slice(0, 5)  
        .join(' ')
        .trim();
      
      if (!normalizedDesc) return;
      
      if (!groups[normalizedDesc]) {
        groups[normalizedDesc] = [];
      }
      
      groups[normalizedDesc].push(ticket);
    });
    
    const repetitive = Object.entries(groups)
      .filter(([_, tickets]) => tickets.length > 1)
      .map(([key, tickets]) => {
        const representativeDescription = tickets[0].short_description || tickets[0].description;
        const category = categorizeDescription(representativeDescription || "");
        
        let totalResolutionTime = 0;
        let ticketsWithResolution = 0;
        
        tickets.forEach(ticket => {
          if ((ticket.resolved_at || ticket.closed_at) && (ticket.created_at || ticket.opened_at || ticket.opened || ticket.created)) {
            const created = new Date(ticket.created_at || ticket.opened_at || ticket.opened || ticket.created);
            const resolved = new Date(ticket.resolved_at || ticket.closed_at);
            
            if (!isNaN(created.getTime()) && !isNaN(resolved.getTime())) {
              totalResolutionTime += (resolved.getTime() - created.getTime());
              ticketsWithResolution++;
            }
          }
        });
        
        const avgResolutionTimeHours = ticketsWithResolution > 0 
          ? Math.round(totalResolutionTime / ticketsWithResolution / 1000 / 60 / 60 * 10) / 10 
          : null;
          
        const incidentTickets = tickets.filter(ticket => 
          ticket.task_type === 'Incident' || 
          (ticket.number?.startsWith('INC'))
        );
        
        const taskTickets = tickets.filter(ticket => 
          ticket.task_type !== 'Incident' &&
          !ticket.number?.startsWith('INC')
        );
        
        return {
          id: key,
          description: representativeDescription,
          category,
          count: tickets.length,
          incidentCount: incidentTickets.length,
          taskCount: taskTickets.length,
          avgResolutionTimeHours,
          assignment_group: tickets[0].assignment_group || tickets[0].service,
          tickets,
          incidentTickets,
          taskTickets
        };
      })
      .sort((a, b) => b.count - a.count);
      
    return repetitive;
  }, [incidents, catalogTasks]);

  const renderTicketCard = (title: string, items: typeof repetitiveItems, type: 'incident' | 'task') => (
    <Card className="mb-4">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>{title}</CardTitle>
        <Badge variant="outline">
          {items.filter(item => type === 'incident' ? item.incidentCount > 0 : item.taskCount > 0).length} patterns detected
        </Badge>
      </CardHeader>
      <CardContent>
        <Collapsible open={isOpen} onOpenChange={setIsOpen} className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium">
              {isOpen ? "Hide" : "Show"} Repetitive Ticket Patterns
            </div>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="w-9 p-0">
                <ChevronDown className={`h-4 w-4 ${isOpen ? "rotate-180" : "rotate-0"} transition-all`} />
              </Button>
            </CollapsibleTrigger>
          </div>
          
          <CollapsibleContent className="space-y-4">
            <Accordion type="multiple" className="w-full">
              {items
                .filter(item => type === 'incident' ? item.incidentCount > 0 : item.taskCount > 0)
                .map((item) => (
                <AccordionItem key={item.id} value={item.id}>
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-left">
                      <div className="flex items-center">
                        <Badge variant="default" className="mr-2">
                          {type === 'incident' ? item.incidentCount : item.taskCount}
                        </Badge>
                        <div className="flex flex-col sm:flex-row sm:items-center">
                          <Badge variant="outline" className="mb-1 sm:mb-0 sm:mr-2">
                            {item.category}
                          </Badge>
                          {/* <span className="font-medium">
                            {generateHeading(type === 'incident' ? item.incidentTickets : item.taskTickets)}
                          </span> */}
                        </div>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Number</TableHead>
                            <TableHead className="max-w-[300px]">Description</TableHead>
                            <TableHead>Opened</TableHead>
                            <TableHead>State</TableHead>
                            <TableHead>Priority</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(type === 'incident' ? item.incidentTickets : item.taskTickets).map((ticket, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{ticket.number || ticket.incident_number || ticket.task_number || "N/A"}</TableCell>
                              <TableCell>
                                <div>
                                  <div className={expandedDescriptions[`${type}-${item.id}-${idx}`] ? "" : "truncate max-w-[300px]"} 
                                       title={ticket.short_description || ticket.description}>
                                    {ticket.short_description || ticket.description || "No description"}
                                  </div>
                                  <Button 
                                    variant="link" 
                                    size="sm" 
                                    className="mt-1 h-auto p-0 text-xs"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleDescription(`${type}-${item.id}-${idx}`);
                                    }}
                                  >
                                    {expandedDescriptions[`${type}-${item.id}-${idx}`] ? "Show less" : "View more"}
                                  </Button>
                                </div>
                              </TableCell>
                              <TableCell>
                                {(() => {
                                  const date = new Date(ticket.opened || ticket.created || ticket.opened_at || ticket.created_at || "");
                                  return isNaN(date.getTime()) ? "Unknown" : format(date, "MMM d, yyyy");
                                })()}
                              </TableCell>
                              <TableCell>
                                <Badge variant={
                                  (ticket.state || ticket.status || "").toLowerCase().includes("close") ? "secondary" : "outline"
                                }>
                                  {ticket.state || ticket.status || "Unknown"}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                {(() => {
                                  const priority = (ticket.priority || "").toLowerCase();
                                  let badgeVariant: "default" | "secondary" | "destructive" | "outline" = "outline";
                                  
                                  if (priority.includes("critical") || priority.includes("1")) {
                                    badgeVariant = "destructive";
                                  } else if (priority.includes("high") || priority.includes("2")) {
                                    badgeVariant = "default";
                                  } else if (priority.includes("medium") || priority.includes("3")) {
                                    badgeVariant = "secondary";
                                  }
                                  
                                  return (
                                    <Badge variant={badgeVariant}>
                                      {ticket.priority || "Unknown"}
                                    </Badge>
                                  );
                                })()}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-4">
      {renderTicketCard("Repetitive Incidents", repetitiveItems, 'incident')}
      {renderTicketCard("Repetitive Tasks", repetitiveItems, 'task')}
    </div>
  );
}
